import { create } from 'zustand';

/**
 * Client state — the things no endpoint returns (CLAUDE.md > Frontend state).
 *
 * Mostly one thing: captures arriving from the Chrome extension (and home's
 * room search, below). They are
 * the one write this app does that nobody is watching happen — the user is on
 * their own page, and the Floto tab is uploading in the background — so
 * "something is on its way" cannot be derived from the server rows. Until it
 * lands there is nothing to fetch, and every screen that lists captures would
 * otherwise say "no captures" while one is mid-upload.
 *
 * Deliberately not the capture itself: the row is server state and belongs to
 * TanStack Query. This holds only what is in flight and what just landed.
 */

export interface LandedCapture {
  projectId: string;
  captureId: string;
  sessionId: string;
}

/* There was a watched-entry type here, feeding a floating pill that narrated
   one agent run over whatever page the user happened to be on. Both are gone.
   A run belongs to one fix-list entry, and that entry's own card already says
   "Floto Agent is working…" underneath it — a second, roaming copy of the same
   sentence was one more thing on screen saying what the list was already
   saying, in a place nobody had asked about it. Progress on a row is read on
   that row. */

interface UiState {
  /** How many extension captures are uploading right now. */
  incoming: number;
  /** The last one to land, so the banner can link to its board. */
  landed: LandedCapture | null;
  /** Set when the last extension capture failed, cleared by the next one. */
  failed: boolean;
  /**
   * Whether the extension answered this page's `ready`.
   *
   * Null until the first announcement could reasonably have arrived — "not
   * installed" and "we have not heard yet" are different screens, and showing
   * install instructions to someone who has it installed is worse than a
   * moment of nothing.
   */
  extensionVersion: null | string;
  extensionSeen: boolean;
  /**
   * The local-capture modal narrates the same captures the notice does, so the
   * notice stands down while it is open rather than the two stacking.
   */
  captureModalOpen: boolean;
  captureIncoming: () => void;
  captureLanded: (capture: LandedCapture) => void;
  captureFailed: () => void;
  dismissCaptureNotice: () => void;
  extensionAnnounced: (version: string) => void;
  setCaptureModalOpen: (open: boolean) => void;
}

const initial = {
  captureModalOpen: false,
  extensionSeen: false,
  extensionVersion: null,
  failed: false,
  incoming: 0,
  landed: null,
};

export const useUiStore = create<UiState>((set) => ({
  ...initial,
  captureFailed: () => {
    set((state) => ({
      failed: true,
      incoming: Math.max(0, state.incoming - 1),
    }));
  },
  captureIncoming: () => {
    set((state) => ({
      failed: false,
      incoming: state.incoming + 1,
      landed: null,
    }));
  },
  captureLanded: (capture) => {
    set((state) => ({
      failed: false,
      incoming: Math.max(0, state.incoming - 1),
      landed: capture,
    }));
  },
  dismissCaptureNotice: () => {
    set({ failed: false, landed: null });
  },
  extensionAnnounced: (version) => {
    set({ extensionSeen: true, extensionVersion: version });
  },
  setCaptureModalOpen: (open) => {
    set({ captureModalOpen: open });
  },
}));

/** The store is a module singleton, so tests must reset it between cases. */
export function resetUiStore(): void {
  useUiStore.setState(initial);
}
